import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { Key } from 'chessground/types';
import { Chess } from 'chess.js';
import AppContext from '../context/AppContext';
import { toDests } from '../utils/ChessUtils';
import deck from './blunder-cards.json';

// Fills the /try wait (about twenty seconds a game) with the product in miniature: one real position,
// one costly move, one guess. Not a tactics trainer: no score, no streak, no next puzzle; the last line
// sends the visitor back to their own games. No server calls: the deck is compiled into the bundle.

// Turns the card on or off. Off because the deck is empty, and an unverified position is worse than no
// card. To turn it on: add verified entries to blunder-cards.json and set this to true.
const SHOW_BLUNDER_CARD = false;

// One position in the deck (blunder-cards.json). No usernames, game ids or dates; a rating band is
// all it says about the players.
//
// Checked with Stockfish at MultiPV 4, depth 20 before it goes in:
//   - the played move loses at least 2.5 pawns against the best move;
//   - the best move beats the second best by at least 1.5 pawns, so there is one answer;
//   - the position is within about 3 pawns of level beforehand.
export type BlunderPosition = {
  // The position before the mistake, with the side to move still to play it.
  fen: string;
  sideToMove: 'white' | 'black';
  // The played move (SAN) and its cost in everyday words ("lost a piece"). No centipawns.
  playedMove: string;
  costInWords: string;
  // The better move (SAN) and one sentence of why it works.
  bestMove: string;
  whyOneSentence: string;
  // All the card says about the players, e.g. "about 1500".
  ratingBand: string;
};

const positions = deck as BlunderPosition[];

// From and to squares of a SAN move, so guesses are graded by squares rather than by string (check
// marks and disambiguation differ).
function squaresOf(fen: string, san: string): [Key, Key] | null {
  try {
    const m = new Chess(fen).move(san);
    return m ? [m.from as Key, m.to as Key] : null;
  } catch {
    return null;
  }
}

type Answer = {
  // SAN of the visitor's move, or null when they asked to be shown.
  guess: string | null;
  correct: boolean;
};

export default function BlunderCard(): React.ReactNode {
  // One position per visit, picked on mount, so the wait doesn't become a puzzle feed.
  const position = useMemo(
    () => (positions.length === 0 ? null : positions[Math.floor(Math.random() * positions.length)]),
    [],
  );
  if (!SHOW_BLUNDER_CARD || position == null) {
    return null;
  }
  return <BlunderCardBody position={position} />;
}

function BlunderCardBody({ position }: { position: BlunderPosition }): React.ReactNode {
  const { siteName } = useContext(AppContext);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  // One guess. Also in a ref because the drag handler is created once.
  const answeredRef = useRef(false);

  const best = useMemo(() => squaresOf(position.fen, position.bestMove), [position]);

  const answered = (a: Answer) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    setAnswer(a);
  };

  // Create the board once, with the side to move at the bottom.
  useEffect(() => {
    if (!boardRef.current) return;
    const chess = new Chess(position.fen);
    const cg = Chessground(boardRef.current, {
      fen: position.fen,
      orientation: position.sideToMove,
      turnColor: position.sideToMove,
      // Coordinates are noise on a board this small.
      coordinates: false,
      animation: { enabled: true, duration: 200 },
      movable: {
        free: false,
        color: position.sideToMove,
        dests: toDests(chess),
        showDests: true,
        events: {
          after: (orig: Key, dest: Key) => {
            let san: string | null = null;
            try {
              san = new Chess(position.fen).move({ from: orig, to: dest, promotion: 'q' })?.san ?? null;
            } catch {
              san = null;
            }
            if (san == null) return;
            answered({
              guess: san,
              correct: best != null && orig === best[0] && dest === best[1],
            });
          },
        },
      },
      draggable: { enabled: true },
      drawable: { enabled: false, visible: true },
    });
    apiRef.current = cg;
    return () => cg.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The reveal: put the position back and draw the better move. The board stops taking moves.
  useEffect(() => {
    const cg = apiRef.current;
    if (!cg || answer == null) return;
    cg.set({
      fen: position.fen,
      turnColor: position.sideToMove,
      movable: { free: false, color: undefined, dests: new Map() },
      draggable: { enabled: false },
    });
    if (best != null) {
      cg.setAutoShapes([{ orig: best[0], dest: best[1], brush: 'green' }]);
    }
  }, [answer, position, best]);

  const sideLabel = position.sideToMove === 'white' ? 'White' : 'Black';

  return (
    <section className="cm-try-blunder" aria-label="A mistake from someone else's game">
      <p className="cm-try-blunder-eyebrow">While the engine reads your games</p>
      <p className="cm-try-blunder-context">
        A real game, {sideLabel} to move, both players rated {position.ratingBand}.
      </p>
      <p className="cm-try-blunder-ask">
        They played <strong className="cm-mono">{position.playedMove}</strong> and{' '}
        {position.costInWords}. What would you have played?
      </p>

      <div className="cm-try-blunder-board" ref={boardRef} />

      {answer == null ? (
        <button
          type="button"
          className="cm-try-blunder-show"
          onClick={() => answered({ guess: null, correct: false })}
        >
          Show me
        </button>
      ) : (
        <div className={`cm-try-blunder-reveal${answer.correct ? ' is-correct' : ''}`}>
          <p className="cm-try-blunder-verdict">
            {answer.correct ? (
              <>
                That&rsquo;s it — <span className="cm-mono">{position.bestMove}</span>.
              </>
            ) : answer.guess != null ? (
              <>
                Not this time: you played <span className="cm-mono">{answer.guess}</span>. The move
                was <span className="cm-mono">{position.bestMove}</span>.
              </>
            ) : (
              <>
                The move was <span className="cm-mono">{position.bestMove}</span>.
              </>
            )}
          </p>
          <p className="cm-try-blunder-why">{position.whyOneSentence}</p>
        </div>
      )}

      <p className="cm-try-blunder-close">
        {siteName === '' ? 'SaveYourChess' : siteName} finds these in your own games and hands them
        back to you.
      </p>
    </section>
  );
}
